import { useEffect, useRef } from 'react';

import { readBarcode } from './read-barcode';
import styles from './styles.module.css';

export interface DecodedSymbol {
	typeName: string;
	data: string;
}

let offscreenCanvas: OffscreenCanvas | undefined;

type ScannerProps = {
	onScan: (code: string) => void;
	ignoreCodes?: Set<string>;
};

export const Scanner = ({ onScan, ignoreCodes }: ScannerProps) => {
	const videoRef = useRef<HTMLVideoElement>(null);

	// Kick off the camera
	useEffect(() => {
		(async () => {
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				audio: false,
				video: { facingMode: 'environment' },
			});

			if (!videoRef.current) throw new Error('Video ref is not set.');
			videoRef.current.srcObject = mediaStream;
		})();
	}, []);

	// Kick off detection
	useEffect(() => {
		console.log('Starting detection');
		let animationHandle: number;

		const detect = async () => {
			// For use with debugging.
			// console.log('Inside detect, Mode: ', mode);

			const video = videoRef.current;
			if (!video) throw new Error('Video ref is not set.');

			if (video.videoHeight && video.videoWidth) {
				if (
					!offscreenCanvas ||
					offscreenCanvas.width !== offscreenCanvas.width ||
					offscreenCanvas.height !== offscreenCanvas.height
				) {
					// Only resizing the canvas caused Chromium to become progressively slower
					offscreenCanvas = new OffscreenCanvas(video.videoWidth, video.videoHeight);
				}

				const context = offscreenCanvas?.getContext('2d', { willReadFrequently: true });
				if (!context) throw new Error('Could not get 2d context from offscreen canvas.');
				context.drawImage(video, 0, 0);
				const imageData = context.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height);
				const symbols = await readBarcode(imageData);

				// This can happen so quickly that we need to ensure we don't spam the notifications.
				// Lock the detectedCodes variable so we know only the first instance will notify.
				for (let i = 0; i < symbols.length; i++) {
					const symbol = symbols[i];

					if (!ignoreCodes?.has(symbol.data)) {
						console.log('Scanned new code: ', symbol.data);
						onScan(symbol.data);
					}
				}
			}

			animationHandle = requestAnimationFrame(detect);
		};

		animationHandle = requestAnimationFrame(detect);

		return () => {
			cancelAnimationFrame(animationHandle);
		};
	}, [videoRef, onScan]);

	return (
		<>
			<video
				ref={videoRef}
				muted
				playsInline
				disablePictureInPicture
				autoPlay
				className={styles.video}
			></video>
		</>
	);
};
